import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import contractsService from '../../../services/compliance/contracts.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import {
  ATTENTION_WINDOW_DAYS,
  AttentionItem,
  attentionItems,
  formatDateOnly,
} from './contractAttention';

const MAX_ROWS = 8;

/**
 * "Needs your attention": the signed-in user's own contracts (owner or
 * renewal owner) with a deadline passed or due in the next 30 days.
 */
export default function NeedsAttentionPanel() {
  const { userOid, loading: accessLoading } = useComplianceAccess();
  const [items, setItems] = useState<AttentionItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (accessLoading || !userOid) return;
    let cancelled = false;
    contractsService
      .list()
      .then((rows) => {
        if (!cancelled) setItems(attentionItems(rows, userOid));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [accessLoading, userOid]);

  if (failed || (!accessLoading && !userOid)) return null;

  const overdue = items?.filter((i) => i.kind === 'overdue').length ?? 0;

  return (
    <section
      aria-label="Needs your attention"
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h2 className="text-base font-semibold text-body">Needs your attention</h2>
        <Link
          to="/compliance/contracts?tab=data&scope=mine"
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          View my contracts
        </Link>
      </div>

      {items === null ? (
        <p className="mt-2 text-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-2 text-sm text-muted">
          You&apos;re all caught up — nothing is due in the next {ATTENTION_WINDOW_DAYS} days.
        </p>
      ) : (
        <>
          <p className="mt-1 text-sm text-muted">
            {overdue > 0 && (
              <span className="font-semibold text-red-700 dark:text-red-300">{overdue} overdue · </span>
            )}
            {items.length - overdue} due in the next {ATTENTION_WINDOW_DAYS} days
          </p>
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
            {items.slice(0, MAX_ROWS).map((item) => (
              <li key={item.contract.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                <Link
                  to={`/compliance/contracts?tab=data&id=${item.contract.id}`}
                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
                >
                  {item.contract.title}
                </Link>
                <span
                  className={`shrink-0 ${
                    item.kind === 'overdue'
                      ? 'font-semibold text-red-700 dark:text-red-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {item.reason} {formatDateOnly(item.date)}
                </span>
              </li>
            ))}
          </ul>
          {items.length > MAX_ROWS && (
            <p className="mt-2 text-xs text-muted">…and {items.length - MAX_ROWS} more.</p>
          )}
        </>
      )}
    </section>
  );
}
